import React, { createContext, useContext, useState } from 'react';

import { DateRange } from '@components/DateRangePicker/DateRangePickerModal';
import { ContactType } from '@components/Invite/Contact';
import {
  MyTasksGroupBy,
  MyTasksOrderBy,
} from '@components/MyTasks/myTasksGrouping';
import {
  Project,
  TaskSortOption,
  Team,
  User,
  TaskPriority,
} from '@graphql/generated';
import { PickEnum } from '@root/types';

export type ProjectFilter = {
  type: 'TasksFilterProject';
  name: string;
  value: Project['id'];
};

export type UserFilter = {
  type: 'TasksFilterUser';
  name: string;
  value: User['id'];
};

export type DueDateFilter = {
  type: 'DueDateFilter';
  name: string;
  value: string;
};

export type DueDateStrFilter = {
  type: 'DueDateStrFilter';
  name: string;
  value: DateRange;
};

export type PriorityFilter = {
  type: 'PriorityFilterUser';
  name: string;
  value: TaskPriority;
};

export type TeamFilter = {
  type: 'TasksFilterTeam';
  name: string;
  value: Team['id'];
};

export type SortFilter = {
  type: 'TasksFilterSortOption';
  name: SortFilterName;
  value: FilterableTaskSortOptions;
};

export enum SortFilterName {
  DueDateAsc = 'Due Date',
  DueDateDesc = 'Reverse Due Date',
  NameDesc = 'Reverse Alphabetical',
  NameAsc = 'Alphabetical',
}

export type SectionFilter = {
  type: 'TasksFilterSectionOption';
  name: string;
  value: TaskSectionOptions;
};

export type FilterItem =
  | ProjectFilter
  | UserFilter
  | PriorityFilter
  | DueDateFilter
  | DueDateStrFilter
  | TeamFilter
  | SortFilter
  | SectionFilter;
export type FilterItems = FilterItem[];

export enum TaskSectionOptions {
  Active = 'Active Tasks',
  Completed = 'Completed Tasks',
}

export enum ProjectTaskSectionOptions {
  Active = 'Active Tasks',
  Completed = 'Completed Tasks',
}

export enum ProjectSectionOptions {
  Active = 'Active Projects',
  Archived = 'Archived Projects',
}

export type FilterableTaskSortOptions = PickEnum<
  TaskSortOption,
  | TaskSortOption.DueDateAsc
  | TaskSortOption.DueDateDesc
  | TaskSortOption.NameAsc
  | TaskSortOption.NameDesc
>;

type tasksFilterContext = {
  sectionOption: TaskSectionOptions;
  setSectionOption: React.Dispatch<React.SetStateAction<TaskSectionOptions>>;
  projectFilters: ProjectFilter[];
  setProjectFilters: React.Dispatch<React.SetStateAction<ProjectFilter[]>>;
  sortByFilter: SortFilter | undefined;
  setSortByFilter: React.Dispatch<React.SetStateAction<SortFilter | undefined>>;
  userFilters: UserFilter[];
  setUserFilters: React.Dispatch<React.SetStateAction<UserFilter[]>>;
  initialAssignee: ContactType[];
  setInitialAssignee: React.Dispatch<React.SetStateAction<ContactType[]>>;
  dueDateFilters: DueDateFilter[];
  setDueDateFilters: React.Dispatch<React.SetStateAction<DueDateFilter[]>>;
  dueDateStrFilters: DueDateStrFilter;
  setDueDateStrFilters: React.Dispatch<React.SetStateAction<DueDateStrFilter>>;
  priorityFilters: PriorityFilter[];
  setPriorityFilters: React.Dispatch<React.SetStateAction<PriorityFilter[]>>;
  teamFilters: TeamFilter[];
  setTeamFilters: React.Dispatch<React.SetStateAction<TeamFilter[]>>;
  projectId: string | undefined;
  setProjectId: React.Dispatch<React.SetStateAction<string | undefined>>;
  projectUUId: string | undefined;
  setProjectUUId: React.Dispatch<React.SetStateAction<string | undefined>>;
  userNotViewer: boolean | undefined;
  userTeamMember: boolean | undefined;
  setUserNotViewer: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  setUserTeamMember: React.Dispatch<React.SetStateAction<boolean | undefined>>;
  // Group-by selection for the My Tasks revamp (TA-10186 AC8). Applies to all
  // 3 tabs at once; the filter bottom sheet (TA-10187) writes this value.
  groupByOption: MyTasksGroupBy;
  setGroupByOption: React.Dispatch<React.SetStateAction<MyTasksGroupBy>>;
  // In-group ordering from the filter sheet's Order By row (TA-10187).
  orderByOption: MyTasksOrderBy;
  setOrderByOption: React.Dispatch<React.SetStateAction<MyTasksOrderBy>>;
  // Clears the My Tasks revamp filter fields (group/order/project/assignee/
  // priority/due-date) back to their initial state — MyTasksScreen calls
  // this on mount so a filter applied on a previous visit never carries over
  // into a fresh app launch or a new login session (mirrors
  // resetProjectTaskFilters in useProjectTaskFilter.tsx).
  resetTasksFilters: () => void;
};

const tasksFilterContext = createContext<tasksFilterContext | undefined>(
  undefined
);

const getInitialDueDateStrFilters = (): DueDateStrFilter => ({
  type: 'DueDateStrFilter',
  name: '',
  value: { start: undefined, end: undefined },
});

export const TasksFilterProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const { Provider } = tasksFilterContext;

  const [projectFilters, setProjectFilters] = useState<ProjectFilter[]>([]);
  const [userFilters, setUserFilters] = useState<UserFilter[]>([]);
  const [initialAssignee, setInitialAssignee] = useState<ContactType[]>([]);
  const [sortByFilter, setSortByFilter] = useState<SortFilter>();
  const [teamFilters, setTeamFilters] = useState<TeamFilter[]>([]);
  const [dueDateFilters, setDueDateFilters] = useState<DueDateFilter[]>([]);
  const [dueDateStrFilters, setDueDateStrFilters] = useState<DueDateStrFilter>(
    getInitialDueDateStrFilters()
  );
  const [priorityFilters, setPriorityFilters] = useState<PriorityFilter[]>([]);

  const [sectionOption, setSectionOption] = useState<TaskSectionOptions>(
    TaskSectionOptions.Active
  );
  const [groupByOption, setGroupByOption] = useState<MyTasksGroupBy>(
    MyTasksGroupBy.Project
  );
  const [orderByOption, setOrderByOption] = useState<MyTasksOrderBy>(
    MyTasksOrderBy.OverdueToLow
  );
  const [projectId, setProjectId] = useState<string>();
  const [projectUUId, setProjectUUId] = useState<string>();
  const [userNotViewer, setUserNotViewer] = useState<boolean>();
  const [userTeamMember, setUserTeamMember] = useState<boolean>();

  const resetTasksFilters = () => {
    setGroupByOption(MyTasksGroupBy.Project);
    setOrderByOption(MyTasksOrderBy.OverdueToLow);
    setProjectFilters([]);
    setUserFilters([]);
    setPriorityFilters([]);
    setDueDateFilters([]);
    setDueDateStrFilters(getInitialDueDateStrFilters());
  };

  return (
    <Provider
      value={{
        sectionOption,
        setSectionOption,
        projectFilters,
        setProjectFilters,
        sortByFilter,
        setSortByFilter,
        userFilters,
        setUserFilters,
        teamFilters,
        setTeamFilters,
        projectId,
        setProjectId,
        projectUUId,
        setProjectUUId,
        userNotViewer,
        setUserNotViewer,
        userTeamMember,
        setUserTeamMember,
        dueDateFilters,
        setDueDateFilters,
        dueDateStrFilters,
        setDueDateStrFilters,
        priorityFilters,
        setPriorityFilters,
        initialAssignee,
        setInitialAssignee,
        groupByOption,
        setGroupByOption,
        orderByOption,
        setOrderByOption,
        resetTasksFilters,
      }}>
      {children}
    </Provider>
  );
};

export const useTasksFilter = (): tasksFilterContext => {
  const context = useContext(tasksFilterContext);
  if (context === undefined) {
    throw new Error('useTasksFilter must be used within a Provider');
  }
  return context;
};
